import {importLargeModuleWithRetry, importWithRetry, versionedModuleUrl} from './app/import-with-retry.js'
import {asBootError, bootFailureMarkup} from './app/boot-screen.js'
import {clearMeteorBootReloadFlag, waitForMeteorRuntime} from './app/wait-for-meteor-runtime.js'
import {isAppEntryPath} from './space-path.js'
import {UI_GLASS_ENABLED_CLASS} from './consts/ui-classes.js'
import type * as MemoryTelemetryModule from './app/memory-telemetry.js'
import type * as MemoryReportsModule from '../imports/collections/MemoryReports.js'
import type * as BusinessEventsModule from '../imports/collections/BusinessEvents.js'

const APP_SHELL_MOUNT_TIMEOUT_MS = 30_000
const BOOT_FAILURE_TIMEOUT_MS = 15_000
const HOME_PAGE_BUNDLE = versionedModuleUrl('/elements/home-page.bundle.js')
const COLLECTIONS = '/imports/collections'

const showBootFailure = (root: HTMLElement, error: Error) => {
	root.classList.add('boot-failure-active')
	root.innerHTML = bootFailureMarkup(error.message)
	root.querySelector<HTMLButtonElement>('.boot-failure-retry')?.addEventListener('click', () => {
		clearMeteorBootReloadFlag()
		location.reload()
	})
}

const startMemoryTelemetry = () => {
	void (async () => {
		try {
			const [
				{logPreviousRunMemoryReport, getPreviousRunMemoryRecord, startMemoryTelemetry: start},
				{reportMemoryRecordToDb},
				{getBusinessSessionId, getBusinessVisitorId},
			] = await Promise.all([
				importWithRetry<typeof MemoryTelemetryModule>('./app/memory-telemetry.js'),
				importWithRetry<typeof MemoryReportsModule>(`${COLLECTIONS}/MemoryReports.js`),
				importWithRetry<typeof BusinessEventsModule>(`${COLLECTIONS}/BusinessEvents.js`),
			])
			logPreviousRunMemoryReport()
			const previousRecord = getPreviousRunMemoryRecord()
			if (previousRecord) {
				reportMemoryRecordToDb({
					record: previousRecord,
					visitorId: getBusinessVisitorId(),
					sessionId: getBusinessSessionId(),
					userAgent: navigator.userAgent,
					url: location.href,
				})
			}
			start()
		} catch (err) {
			console.warn('Memory telemetry failed (non-fatal):', err)
		}
	})()
}

const bootAppShell = async () => {
	const root = document.getElementById('root')

	// Safety net: if Meteor.startup() never fires (DDP/WebSocket down), show failure
	// instead of leaving the splash stuck forever.
	const bootFailureTimer = window.setTimeout(() => {
		if (root?.querySelector('#boot-splash')) showBootFailure(root, new Error('Could not connect to server'))
	}, BOOT_FAILURE_TIMEOUT_MS)

	try {
		await waitForMeteorRuntime(8_000)
	} catch (err) {
		window.clearTimeout(bootFailureTimer)
		const error = asBootError(err as object)
		console.error('Meteor runtime failed to load', error)
		if (root) showBootFailure(root, error)
		return
	}

	const {Meteor} = await import('meteor/meteor')

	Meteor.startup(async () => {
		window.clearTimeout(bootFailureTimer)
		document.documentElement.classList.toggle(UI_GLASS_ENABLED_CLASS, true)
		if (!root) return

		try {
			await Promise.race([
				importLargeModuleWithRetry(HOME_PAGE_BUNDLE),
				new Promise<never>((_, reject) =>
					window.setTimeout(() => reject(new Error('App shell mount timed out')), APP_SHELL_MOUNT_TIMEOUT_MS),
				),
			])
			root.innerHTML = `<home-page></home-page>`
			startMemoryTelemetry()
			clearMeteorBootReloadFlag()
		} catch (err) {
			const error = asBootError(err as object)
			console.error('Failed to mount app shell', error)
			showBootFailure(root, error)
		}
	})
}

if (isAppEntryPath(location.pathname)) void bootAppShell()

export {}
